import { useEffect, useState } from 'react'

const DEFAULT_MIN_PANEL_WIDTH = 100

function applyPanelWidths(leftPanel, rightPanel, leftWidth, rightWidth) {
  leftPanel.style.width = `${leftWidth}px`
  leftPanel.style.flexBasis = `${leftWidth}px`
  leftPanel.style.flexGrow = '0'
  leftPanel.style.flexShrink = '0'

  rightPanel.style.width = `${rightWidth}px`
  rightPanel.style.flexBasis = `${rightWidth}px`
  rightPanel.style.flexGrow = '0'
  rightPanel.style.flexShrink = '0'
}

/**
 * useResizable Hook
 *
 * A clean, SRP-compliant hook for implementing split-pane resize functionality.
 * Handles drag-to-resize behavior between two panels with a draggable separator.
 *
 * @param {Object} params
 * @param {React.RefObject} params.refDragger - Ref to the draggable separator element
 * @param {React.RefObject} params.refLeftPanel - Ref to the left panel element
 * @param {React.RefObject} params.refRightPanel - Ref to the right panel element
 * @param {boolean} [params.disabled=false] - Disables resize functionality when true
 *
 * @returns {Object} { isResizing: boolean } - Current resize state
 *
 * @example
 * const leftPanelRef = useRef()
 * const rightPanelRef = useRef()
 * const draggerRef = useRef()
 *
 * const { isResizing } = useResizable({
 *   refDragger: draggerRef,
 *   refLeftPanel: leftPanelRef,
 *   refRightPanel: rightPanelRef,
 *   disabled: false
 * })
 *
 * // In JSX:
 * <div ref={leftPanelRef} style={{ width: '50%', flexShrink: 0 }}>Left Panel</div>
 * <div ref={draggerRef} style={{ cursor: 'ew-resize' }}>Dragger</div>
 * <div ref={rightPanelRef} style={{ width: '50%', flexShrink: 0 }}>Right Panel</div>
 */
export default function useResizable({
  refDragger,
  refLeftPanel,
  refRightPanel,
  minLeftWidth = DEFAULT_MIN_PANEL_WIDTH,
  minRightWidth = DEFAULT_MIN_PANEL_WIDTH,
  disabled = false,
}) {
  const [isResizing, setIsResizing] = useState(false)
  const [refsReady, setRefsReady] = useState(false)

  // Check if refs are ready and trigger re-run when they become available
  useEffect(() => {
    const checkRefs = () => {
      const dragger = refDragger?.current
      const leftPanel = refLeftPanel?.current
      const rightPanel = refRightPanel?.current

      if (dragger && leftPanel && rightPanel) {
        setRefsReady(true)
      }
    }

    // Check immediately
    checkRefs()

    // Set up an interval to check periodically until refs are ready
    if (!refsReady) {
      const interval = setInterval(checkRefs, 100)
      return () => clearInterval(interval)
    }
  }, [refDragger, refLeftPanel, refRightPanel, refsReady])

  useEffect(() => {
    if (disabled || !refsReady) return

    const dragger = refDragger?.current
    const leftPanel = refLeftPanel?.current
    const rightPanel = refRightPanel?.current

    if (!dragger || !leftPanel || !rightPanel) return

    let startX = 0
    let startLeftWidth = 0
    let startRightWidth = 0

    const getAvailableWidth = () => {
      const separatorWidth = dragger.getBoundingClientRect().width || dragger.offsetWidth || 0
      const containerWidth =
        dragger.parentElement?.getBoundingClientRect().width ||
        leftPanel.offsetWidth + rightPanel.offsetWidth + separatorWidth

      return Math.max(minLeftWidth + minRightWidth, containerWidth - separatorWidth)
    }

    const getNextWidths = (deltaX) => {
      const availableWidth = getAvailableWidth()
      const minDelta = minLeftWidth - startLeftWidth
      const maxDelta = startRightWidth - minRightWidth
      const boundedDelta = Math.min(maxDelta, Math.max(minDelta, deltaX))
      const nextLeftWidth = Math.min(
        availableWidth - minRightWidth,
        Math.max(minLeftWidth, startLeftWidth + boundedDelta),
      )
      const nextRightWidth = Math.max(minRightWidth, availableWidth - nextLeftWidth)

      return { nextLeftWidth, nextRightWidth }
    }

    /**
     * Handle mouse movement during drag
     * Calculates new panel widths and applies them in real-time
     */
    const handleMouseMove = (e) => {
      const deltaX = e.clientX - startX
      const { nextLeftWidth, nextRightWidth } = getNextWidths(deltaX)
      applyPanelWidths(leftPanel, rightPanel, nextLeftWidth, nextRightWidth)
    }

    /**
     * Handle mouse up - clean up event listeners and reset styles
     */
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      window.removeEventListener('blur', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      setIsResizing(false)
    }

    /**
     * Handle mouse down on dragger - start resize operation
     */
    const handleMouseDown = (e) => {
      e.preventDefault()

      // Store initial state
      startX = e.clientX
      startLeftWidth = leftPanel.offsetWidth
      startRightWidth = rightPanel.offsetWidth

      // Set drag cursor and disable text selection
      document.body.style.cursor = 'ew-resize'
      document.body.style.userSelect = 'none'

      // Add document-level event listeners for smooth dragging
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      window.addEventListener('blur', handleMouseUp)

      setIsResizing(true)
    }

    // Attach initial event listener
    dragger.addEventListener('mousedown', handleMouseDown)

    // Cleanup function
    return () => {
      dragger.removeEventListener('mousedown', handleMouseDown)
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      window.removeEventListener('blur', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [
    refDragger,
    refLeftPanel,
    refRightPanel,
    minLeftWidth,
    minRightWidth,
    disabled,
    refsReady
  ])

  return { isResizing }
}
